import { useState, useRef } from "react";
import { Bug, Send, Paperclip, X, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/services/logger-service";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface BugReportDialogProps {
  compact?: boolean;
}

export function BugReportDialog({ compact = false }: BugReportDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"chat" | "report">("chat");
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleAiChat = async () => {
    if (!userInput.trim()) return;

    const newUserMessage: Message = { role: "user", content: userInput };
    setChatMessages((prev) => [...prev, newUserMessage]);
    setUserInput("");
    setIsProcessing(true);

    try {
      const logs = logger.getLogs().slice(-50);
      
      const { data, error } = await supabase.functions.invoke("bug-diagnostic-chat", {
        body: {
          userMessage: userInput,
          conversationHistory: chatMessages,
          systemContext: {
            currentUrl: window.location.href,
            userAgent: navigator.userAgent,
            recentLogs: logs,
          },
        },
      });

      if (error) throw error;

      const aiMessage: Message = { role: "assistant", content: data.message };
      setChatMessages((prev) => [...prev, aiMessage]);
    } catch (error: any) {
      console.error("Error in AI chat:", error);
      toast({
        title: "Chat error",
        description: "Failed to get AI response. You can still submit a bug report.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (attachments.length + files.length > 5) {
      toast({
        title: "Too many files",
        description: "You can attach up to 5 files.",
        variant: "destructive",
      });
      return;
    }
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadAttachments = async (userId: string): Promise<string[]> => {
    if (attachments.length === 0) return [];

    const uploadedUrls: string[] = [];

    for (const file of attachments) {
      const fileName = `${userId}/${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from("bug-attachments")
        .upload(fileName, file);

      if (error) {
        console.error("Failed to upload attachment:", error);
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("bug-attachments")
        .getPublicUrl(fileName);

      uploadedUrls.push(publicUrl);
    }

    return uploadedUrls;
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast({
        title: "Description required",
        description: "Please describe the issue you're experiencing.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Upload attachments
      const attachmentUrls = await uploadAttachments(user.id);

      // Get recent logs
      const logs = logger.getLogs().slice(-50);

      // Send bug report
      const { error } = await supabase.functions.invoke("send-bug-report", {
        body: {
          userEmail: user.email || "unknown",
          userName: user.user_metadata?.name || user.user_metadata?.full_name || "Unknown User",
          userRole: user.user_metadata?.role || "unknown",
          userId: user.id,
          description,
          logs,
          currentUrl: window.location.href,
          userAgent: navigator.userAgent,
          attachmentUrls,
          aiConversation: chatMessages,
        },
      });

      if (error) throw error;

      toast({
        title: "Bug report sent",
        description: "Thank you for your feedback! We'll look into this issue.",
      });

      logger.info("Bug report submitted successfully", {
        component: "BugReportDialog",
        action: "submit_bug_report",
        hasAiConversation: chatMessages.length > 0,
        attachmentCount: attachmentUrls.length,
      });

      // Reset state
      setDescription("");
      setAttachments([]);
      setChatMessages([]);
      setStep("chat");
      setOpen(false);
    } catch (error: any) {
      console.error("Error submitting bug report:", error);
      
      logger.error("Failed to submit bug report", {
        component: "BugReportDialog",
        action: "submit_bug_report",
        error: error.message,
      });

      toast({
        title: "Failed to send bug report",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const proceedToReport = () => {
    // Pre-fill description with a summary of the chat
    if (chatMessages.length > 0) {
      const summary = chatMessages
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join("\n\n");
      setDescription(summary);
    }
    setStep("report");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Bug className="h-4 w-4" />
          {!compact && "Report Bug"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        {step === "chat" ? (
          <>
            <DialogHeader>
              <DialogTitle>Need Help?</DialogTitle>
              <DialogDescription>
                Let's figure out what's going on. Describe the issue and I'll try to help before submitting a bug report.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 flex flex-col gap-4 py-4 min-h-0">
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4">
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <p>Hi! I'm here to help diagnose any issues you're experiencing.</p>
                      <p className="text-sm mt-2">Describe what happened and I'll ask some questions.</p>
                    </div>
                  ) : (
                    chatMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                  {isProcessing && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-lg px-4 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              <div className="flex gap-2">
                <Input
                  placeholder="Describe what happened..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleAiChat()}
                  disabled={isProcessing}
                />
                <Button onClick={handleAiChat} disabled={isProcessing || !userInput.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <p className="text-sm text-muted-foreground">
                  {chatMessages.length > 0 ? "Still need help?" : "Ready to report?"}
                </p>
                <Button onClick={proceedToReport} variant="secondary">
                  Submit Bug Report
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Submit Bug Report</DialogTitle>
              <DialogDescription>
                We'll include your conversation, logs, and any attachments.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 flex flex-col gap-4 py-4 min-h-0">
              <Textarea
                placeholder="Add any additional details about the issue..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                className="resize-none"
              />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attachments.length >= 5}
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    Attach Files
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    ({attachments.length}/5)
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.txt,.log"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((file, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between bg-muted px-3 py-2 rounded"
                      >
                        <span className="text-sm truncate flex-1">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(i)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-between gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => setStep("chat")}
                  disabled={isSubmitting}
                >
                  Back to Chat
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Report"}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
